<script>
import { DropdownComponent } from 'vue-elder-dropdown'
import { LoaderComponent as Loader } from 'vue-elder-dropdown'
import { InputComponent } from 'vue-elder-input'
import Dropdown from './Actions/Dropdown.vue'
import { Debounce } from '@/utils'
import fetch from '@/fetch'

export default {
  props: {
    value: Array,
    addLabel: String,
    items: String,
    searchable: Boolean,
    ignored: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      fetchedTags: [],
      search: '',
      activeTag: null,
      isActive: false,
      promises: {
        fetch: null,
      },
    }
  },
  watch: {
    search: 'fetchDebounced',
    isActive: 'fetch',
  },
  computed: {
    isAddable() {
      return this.search && !this.availableTags.find(a => a === this.search)
    },
    availableTags() {
      return this.fetchedTags.filter(f => !this.value.includes(f))
    },
    filteredTags() {
      return this.value.filter(v => !this.ignored.some(i => new RegExp(i).test(v)))
    },
  },
  methods: {
    onClickAway(e) {
      if (!this.activeTag) return
      const activeTagEl = this.$el.querySelector(`[data-tag="${this.activeTag}"]`)
      if (activeTagEl && !activeTagEl.contains(e.target)) this.activeTag = null
    },
    fetchDebounced: Debounce(function () {
      this.fetch()
    }, 300),
    fetch() {
      if (!this.items || !this.isActive) return

      const url = new URL(this.items, location.origin)
      if (this.search) url.searchParams.set('search', this.search)

      return (this.promises.fetch = fetch(url.toString())
        .then(res => res.json())
        .then(data => (this.fetchedTags = data.sort())))
    },
    add(tag, close) {
      if (!this.value.find(v => v === tag)) this.$emit('input', [...this.value, tag])
      return close()
    },
    remove(tag) {
      if (!this.value.find(v => v === tag)) return
      this.$emit(
        'input',
        this.value.filter(v => v !== tag),
      )
    },
    onExistingTagClick(tag) {
      if (this.activeTag !== tag) this.activeTag = tag
      else this.remove(tag)
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickAway)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickAway)
  },
  components: {
    DropdownComponent,
    InputComponent,
  },
}
</script>

<template>
  <div class="tags">
    <ButtonComponent
      v-for="tag in filteredTags"
      :label="tag"
      class="tags__tag"
      :data-tag="tag"
      @click="() => onExistingTagClick(tag)"
      :theme="activeTag === tag ? 'error' : 'default'"
      :icon="tag === activeTag ? 'fa-pro-light:trash' : null"
    />
    <DropdownComponent :close-on-click="false" @visible="() => (isActive = true)" @hidden="() => (isVisible = false)">
      <ButtonComponent icon="fa-pro-light:plus" :label="addLabel" class="tags__tag" />
      <template #dropdown="{ close }">
        <InputComponent v-model="search" :placeholder="$t('typeHere') + '...'" icon="fa-pro-light:search" />
        <Loader :value="promises.fetch" class="tags__items">
          <ButtonComponent
            v-if="isAddable"
            icon="fa-pro-light:plus"
            class="tags__tag"
            @click="() => add(search, close)"
            :label="search"
          />
          <ButtonComponent
            v-for="tag in availableTags"
            icon="fa-pro-light:plus"
            class="tags__tag"
            @click="() => add(tag, close)"
            :label="tag"
          />
          <span data-part="no-tags">
            {{
              [!availableTags.length && !search ? $t('noTags') : null, !search ? $t('tagAddInstruction') : null]
                .filter(Boolean)
                .join(' — ')
            }}
          </span>
        </Loader>
      </template>
    </DropdownComponent>
  </div>
</template>

<style lang="scss">
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;

  .elder-dropdown__content {
    min-width: 250px;
  }

  .elder-dropdown__content-box {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    --vue-elder-button-padding-y: 5px !important;
    --vue-elder-button-padding-x: 10px !important;

    .elder-button__label {
      padding-left: var(--vue-elder-button-padding-x) !important;
    }
    .elder-button__icon {
      padding-right: var(--vue-elder-button-padding-x) !important;
    }
  }

  .elder-input__field {
    border-radius: 99px;
  }

  &__items {
    font-size: 1rem;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.5rem;
  }

  [data-part='no-tags'] {
    text-align: center;
    opacity: 0.8;
    font-weight: normal !important;
    display: block;
    width: 100%;
    font-size: 0.8em;
    margin-block: 0.5rem;
  }
}

.tags__tag {
  border-radius: 99px;
  font-size: 0.8em !important;
  border: 1px solid var(--vue-elder-default-border, rgba(0, 0, 0, 0.1)) !important;
  width: initial !important;

  background-color: transparent !important;

  --vue-elder-button-padding-y: 2px;
  --vue-elder-button-padding-x: 7px;

  .elder-button__label {
    padding-left: var(--vue-elder-button-padding-x) !important;
  }
  .elder-button__icon {
    padding-right: var(--vue-elder-button-padding-x) !important;
  }
}
</style>
